import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { MENU_ITEMS } from '../types';
import {
  LayoutDashboard, FileSpreadsheet, Users, Calculator,
  Upload, FileText, UserCog, Shield, Receipt, LogOut,
  Menu, X, ChevronRight, Radio, Settings, User, BarChart3
} from 'lucide-react';

/** 系统版本号 */
const APP_VERSION = 'v86';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard, FileSpreadsheet, Users, Calculator,
  Upload, FileText, UserCog, Shield, Receipt, BarChart3,
};

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, logout, permissions, checkPermission } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); };

  return (
    <div className="flex h-screen bg-[#0F172A] text-[#F1F5F9] overflow-hidden">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-[#1E293B] border-r border-[#334155] flex flex-col transition-transform duration-300 flex-shrink-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ width: 'clamp(180px, 16vw, 260px)' }}
      >
        <div className="h-16 flex items-center px-5 border-b border-[#334155] bg-[#1E293B] relative z-10">
          <img 
            src="/assets/logo.png" 
            alt="余兮目成传媒" 
            className="h-10 rounded"
          />
          <button className="ml-auto lg:hidden text-[#94A3B8]" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 侧边栏用户信息已移至右上角header */}

        <nav className="flex-1 py-4 px-3 overflow-y-auto scrollbar-thin">
          {(() => {
            // 添加调试信息
            console.log('[Layout] 用户信息:', { user: user?.name, role: user?.role, isAdmin: isAdmin });
            console.log('[Layout] 所有菜单:', MENU_ITEMS);
            
            // 路径到权限 page key 的映射
            const pathToPage: Record<string, string> = {
              '/report': 'report',
              '/wage-declaration': 'wage-declaration',
              '/brokers': 'brokers',
              '/formulas': 'formulas',
              '/import': 'import',
              '/report-template': 'report-template',
              '/users': 'users',
              '/roles': 'roles',
              '/bills': 'bills',
            };
            
            // 过滤菜单
            const filteredMenus = MENU_ITEMS.filter(item => {
              // 管理员可以看到所有页面
              if (isAdmin) return true;
              
              // 获取当前页面对应的权限 key
              const pageKey = pathToPage[item.path];
              
              // 添加调试日志
              console.log(`[Layout] 菜单: ${item.path}, pageKey: ${pageKey}, permissions[pageKey]:`, permissions[pageKey]);
              
              // 如果有权限配置，优先使用权限配置判断
              if (pageKey && permissions[pageKey]) {
                const canView = permissions[pageKey].can_view === true;
                console.log(`[Layout] 权限判断: ${item.path} can_view=${canView}`);
                return canView;
              }
              
              // 如果没有权限配置，使用 adminOnly 字段判断
              // adminOnly: false 表示所有人可见
              // adminOnly: true 表示只有管理员可见（但管理员已经在前面返回了）
              const result = !item.adminOnly;
              console.log(`[Layout] adminOnly判断: ${item.path} adminOnly=${item.adminOnly} result=${result}`);
              return result;
            });
            
            console.log('[Layout] 过滤后菜单:', filteredMenus);
            return filteredMenus.map(item => {
              const active = location.pathname === item.path;
              const Icon = ICON_MAP[item.icon];
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center h-11 px-4 rounded-lg mb-1 transition-all duration-200 text-sm ${
                    active
                      ? 'bg-[#3B82F6]/15 text-[#3B82F6] border-l-[3px] border-[#3B82F6]'
                      : 'text-[#94A3B8] hover:bg-[#334155]/50 hover:text-[#F1F5F9]'
                  }`}
                >
                  {Icon && <Icon className="w-5 h-5 mr-3 flex-shrink-0" />}
                  <span>{item.label}</span>
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            });
          })()}
        </nav>

        <div className="p-3 border-t border-[#334155]">
          <button
            onClick={handleLogout}
            className="flex items-center w-full h-10 px-4 rounded-lg text-[#94A3B8] hover:bg-[#334155]/50 hover:text-[#F1F5F9] transition-all text-sm"
          >
            <LogOut className="w-5 h-5 mr-3" />
            <span>退出登录</span>
          </button>
          <div className="text-center mt-2 text-[10px] text-[#475569]">{APP_VERSION}</div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 主内容区域的头部 - 使用sticky定位 */}
        <header className="sticky top-0 h-16 flex items-center justify-between px-4 lg:px-6 border-b border-[#334155] bg-[#0F172A]/95 backdrop-blur-md flex-shrink-0 z-10">
          <button className="lg:hidden text-[#94A3B8] p-2" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          {/* 右侧固定的用户信息区域 */}
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-sm text-[#94A3B8]">{new Date().toLocaleDateString('zh-CN')}</div>
            {user && (
              <div className="flex items-center gap-3">
                <Link to="/profile" className="flex items-center gap-2 hover:bg-[#334155]/50 rounded-lg px-3 py-1.5 transition-all">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium leading-tight">{user.name || user.username}</div>
                    <div className="text-[10px] text-[#94A3B8] leading-tight">{user.role || '经纪人'}</div>
                  </div>
                </Link>
                <button onClick={handleLogout} className="text-[#94A3B8] hover:text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg transition-all" title="退出登录">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </header>
        {/* 主内容区域 */}
        <main className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 lg:py-6 pt-8 lg:pt-10">{children}</main>
      </div>
    </div>
  );
}

export function AuthGuard({ children, adminOnly = false, page }: { children: React.ReactNode; adminOnly?: boolean; page?: string }) {
  const { user, loading, isAdmin, checkPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0F172A] text-[#F1F5F9]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#94A3B8]">加载中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0F172A] text-[#F1F5F9]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#94A3B8]">重定向到登录页...</p>
        </div>
      </div>
    );
  }

  // 基于权限的页面访问控制
  if (adminOnly && !isAdmin) {
    // 如果有指定 page，检查权限
    if (page && checkPermission(page, 'view')) {
      return <Layout>{children}</Layout>;
    }
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-[#94A3B8]">无权访问此页面</p>
            <p className="text-sm text-[#64748B] mt-2">需要权限</p>
          </div>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
}
